import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { PageHeader, btnPrimary, btnTurq, btnGhost, TierBadge, EmptyState, inputCls } from "../../components/bits";
import { Plus, Upload, FileSpreadsheet, Search } from "lucide-react";
import { SchoolCard } from "../../components/admin/SchoolCard";
import { SchoolFormModal } from "../../components/admin/SchoolFormModal";
import { CoordEditModal } from "../../components/admin/CoordEditModal";
import { SchoolImportModal, SchoolsReportModal } from "../../components/admin/SchoolImportModals";

const emptySchool = { name: "", stage: "ثانوية", sector: "", office: "", gender: "بنين", authority: "حكومي",
  address: "", school_phone: "", principal_name: "", staff_count: "", total_students: "" };
const TIER_TITLES = {
  small: "الشريحة الأولى — المدارس الصغرى (1 – 250 طالباً)",
  medium: "الشريحة الثانية — المدارس المتوسطة (251 – 500 طالب)",
  large: "الشريحة الثالثة — المدارس الكبرى (أكثر من 500 طالب)",
};
const STAFF_TIER_TITLES = {
  small: "الشريحة الأولى — المدارس الصغرى (1 – 20 منسوباً)",
  medium: "الشريحة الثانية — المدارس المتوسطة (21 – 50 منسوباً)",
  large: "الشريحة الثالثة — المدارس الكبرى (أكثر من 50 منسوباً)",
};
const SECTIONS = [
  { key: "students", title: "المدارس الثانوية — منافسة الطلاب", titles: TIER_TITLES },
  { key: "staff", title: "المراحل الأخرى (روضة / ابتدائية / متوسطة) — منافسة المنسوبين", titles: STAFF_TIER_TITLES },
];
const STAGE_CHIPS = ["", "ثانوية", "متوسطة", "ابتدائية", "روضة"];
const normalizeAr = (s) => (s || "").replace(/[أإآ]/g, "ا").replace(/ة/g, "ه").replace(/ى/g, "ي").replace(/\s+/g, " ").trim().toLowerCase();
const matchesQuery = (s, q) => {
  const nq = normalizeAr(q);
  if (!nq) return true;
  const c = s.coordinator || {};
  return normalizeAr(`${s.school.name} ${s.school.office || ""} ${s.school.principal_name || ""} ${s.school.school_phone || ""} ${c.name || ""} ${c.email || ""} ${c.phone || ""}`).includes(nq);
};

export default function AdminSchools() {
  const [schools, setSchools] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptySchool);
  const [coordModal, setCoordModal] = useState(null);
  const [coordForm, setCoordForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [schoolCoord, setSchoolCoord] = useState({ name: "", email: "", phone: "" });
  const [noCoordOnly, setNoCoordOnly] = useState(false);
  const [stageFilter, setStageFilter] = useState("");
  const [query, setQuery] = useState("");
  const [sectors, setSectors] = useState([]);
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/admin/schools").then((r) => setSchools(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/sectors").then((r) => setSectors(r.data.sectors)).catch(() => {}); }, []);

  useEffect(() => {
    if (!schools || !location.hash) return;
    const h = location.hash;
    if (h === "#no-coord") {
      setNoCoordOnly(true);
      setStageFilter("");
      return;
    }
    const tierMatch = h.match(/^#tier-(students|staff)-(small|medium|large)$/);
    if (tierMatch) {
      setStageFilter("");
      document.getElementById(`tier-${tierMatch[1]}-${tierMatch[2]}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const stageMatch = h.match(/^#stage-(.+)$/);
    if (stageMatch) {
      setNoCoordOnly(false);
      setStageFilter(decodeURIComponent(stageMatch[1]));
      document.getElementById("schools-section-staff")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setStageFilter("");
    const key = h === "#sec-staff" ? "staff" : h === "#sec-secondary" ? "students" : null;
    if (key) document.getElementById(`schools-section-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, schools]);

  const doImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("اختر ملف Excel أولاً");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/schools/import", fd);
      setReport(data);
      setImportOpen(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, total_students: Number(form.total_students) || 0, staff_count: Number(form.staff_count) || 0 };
      if (modal?.mode === "edit") {
        const coord = modal.school.coordinator;
        if (coord) {
          await api.put(`/admin/coordinators/${coord.id}`, {
            name: schoolCoord.name.trim() || coord.name, email: (schoolCoord.email || coord.email).trim(),
            password: "", school_id: modal.school.school.id, phone: schoolCoord.phone || "",
          });
        }
        await api.put(`/admin/schools/${modal.school.school.id}`, payload);
        toast.success(coord ? "تم تحديث المدرسة وبيانات المنسق" : "تم تحديث المدرسة");
      } else {
        await api.post("/admin/schools", payload);
        toast.success("تمت إضافة المدرسة");
      }
      setModal(null);
      setForm(emptySchool);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveCoord = async () => {
    setBusy(true);
    try {
      if (coordModal.mode === "assign") {
        await api.post(`/admin/schools/${coordModal.school.school.id}/assign-coordinator`, {
          email: coordForm.email.trim(), name: coordForm.name.trim(), phone: coordForm.phone || "",
        });
        toast.success("تم ربط المنسق بالمدرسة");
      } else {
        await api.put(`/admin/coordinators/${coordModal.coord.id}`, {
          name: coordForm.name.trim(), email: coordForm.email || coordModal.coord.email,
          password: coordForm.password || "", school_id: coordModal.school.school.id,
          phone: coordForm.phone || "",
        });
        toast.success("تم تحديث بيانات المنسق");
      }
      setCoordModal(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openCoordEdit = (s) => {
    if (s.coordinator) {
      setCoordForm({ name: s.coordinator.name || "", email: s.coordinator.email || "", phone: s.coordinator.phone || "", password: "" });
      setCoordModal({ mode: "edit", school: s, coord: s.coordinator });
    } else {
      setCoordForm({ name: "", email: "", phone: "", password: "" });
      setCoordModal({ mode: "assign", school: s });
    }
  };

  const openEdit = (s) => {
    setForm({ name: s.school.name, stage: s.school.stage || "ثانوية", sector: s.school.sector || "", office: s.school.office || "",
      gender: s.school.gender || "بنين", authority: s.school.authority || "حكومي", address: s.school.address || "",
      school_phone: s.school.school_phone || "", principal_name: s.school.principal_name || "",
      staff_count: s.school.staff_count ?? "", total_students: s.school.total_students ?? "" });
    setSchoolCoord({ name: s.coordinator?.name || "", email: s.coordinator?.email || "", phone: s.coordinator?.phone || "" });
    setModal({ mode: "edit", school: s });
  };

  const remove = async (s) => {
    if (!window.confirm(`حذف «${s.school.name}» وجميع بياناتها؟`)) return;
    try {
      await api.delete(`/admin/schools/${s.school.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const impersonate = async (s) => {
    try {
      await api.post("/admin/impersonate", { school_id: s.school.id });
      window.location.href = "/portal";
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!schools) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  const noCoordCount = schools.filter((s) => !s.coordinator).length;

  return (
    <div data-testid="admin-schools-page">
      <PageHeader title="المدارس والشرائح" sub="الثانويات تتنافس بأعداد الطلاب، والمراحل الأخرى تتنافس بأعداد المنسوبين — كل شريحة تتنافس مع نظيراتها">
        <button data-testid="schools-template-btn" onClick={() => window.open(`${API}/admin/schools/template`, "_blank")} className={btnGhost}>
          <FileSpreadsheet className="w-4 h-4" /> قالب المدارس
        </button>
        <button data-testid="schools-import-btn" onClick={() => setImportOpen(true)} className={btnTurq}>
          <Upload className="w-4 h-4" /> استيراد Excel
        </button>
        <button data-testid="school-add-btn" onClick={() => { setForm(emptySchool); setModal({ mode: "add" }); }} className={btnPrimary}>
          <Plus className="w-4 h-4" /> إضافة مدرسة
        </button>
      </PageHeader>

      <div className="bg-surface rounded-2xl border border-brd shadow-sm p-3 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-mutext pointer-events-none" />
          <input data-testid="schools-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="بحث سريع: اسم المدرسة، المنسق، المكتب، الجوال أو البريد…" className={`${inputCls} pr-9`} />
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="stage-chips">
          {STAGE_CHIPS.map((st) => (
            <button key={st || "all"} data-testid={`stage-chip-${st || "all"}`} onClick={() => setStageFilter(st)}
              className={`rounded-full px-3.5 py-2 text-[12px] font-bold transition-all ${stageFilter === st
                ? "bg-turq-600 text-white shadow-md"
                : "bg-beige-300 dark:bg-surface2 text-mutext hover:scale-105"}`}>
              {st || "كل المراحل"}
            </button>
          ))}
        </div>
      </div>
      {query && (
        <div data-testid="search-results-count" className="text-xs font-bold text-turq-900 dark:text-turq-300 mb-4">
          نتائج البحث عن «{query}»: {schools.filter((s) => matchesQuery(s, query)).length} مدرسة
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-8">
        <div data-testid="stat-total-schools" className="bg-surface rounded-2xl border border-brd p-4 text-center shadow-sm">
          <div className="font-head font-black text-2xl text-turq-600">{schools.length}</div>
          <div className="text-[11px] text-mutext font-bold mt-0.5">إجمالي المدارس</div>
        </div>
        <div data-testid="stat-schools-with-coord" className="bg-surface rounded-2xl border border-brd p-4 text-center shadow-sm">
          <div className="font-head font-black text-2xl text-turq-600">{schools.length - noCoordCount}</div>
          <div className="text-[11px] text-mutext font-bold mt-0.5">مرتبطة بمنسق</div>
        </div>
        <button data-testid="filter-no-coord-btn" onClick={() => setNoCoordOnly((v) => !v)}
          className={`rounded-2xl border p-4 text-center shadow-sm transition-all ${noCoordOnly
            ? "bg-coral-600 border-coral-600 text-white scale-[1.02]"
            : "bg-surface border-brd hover:scale-[1.02]"}`}>
          <div className={`font-head font-black text-2xl ${noCoordOnly ? "text-white" : "text-coral-600"}`}>{noCoordCount}</div>
          <div className={`text-[11px] font-bold mt-0.5 ${noCoordOnly ? "text-white/90" : "text-mutext"}`}>
            {noCoordOnly ? "بدون منسق — اضغط لإلغاء التصفية" : "بدون منسق — اضغط للتصفية"}
          </div>
        </button>
      </div>

      {SECTIONS.map((section) => {
        const sectionSchools = schools.filter((s) => (s.mode === "staff") === (section.key === "staff") && (!noCoordOnly || !s.coordinator)
          && (!stageFilter || s.school.stage === stageFilter) && matchesQuery(s, query));
        if ((query || stageFilter || noCoordOnly) && sectionSchools.length === 0) return null;
        return (
          <div key={section.key} id={`schools-section-${section.key}`} data-testid={`schools-section-${section.key}`} className="mb-10 scroll-mt-6">
            <h2 className="font-head font-black text-lg mb-4 text-turq-900 dark:text-turq-300 flex items-center gap-2 flex-wrap">
              {section.title} — {sectionSchools.length} مدارس
              {section.key === "staff" && stageFilter && stageFilter !== "ثانوية" && (
                <button data-testid="stage-filter-clear" onClick={() => setStageFilter("")}
                  className="text-[11px] font-bold bg-turq-600 text-white rounded-full px-3 py-1 hover:bg-turq-900 transition-colors">
                  {stageFilter} ✕ إظهار الكل
                </button>
              )}
            </h2>
            {["small", "medium", "large"].map((tier) => {
              const list = sectionSchools.filter((s) => s.tier === tier);
              return (
                <div key={tier} id={`tier-${section.key}-${tier}`} data-testid={`tier-block-${section.key}-${tier}`} className="mb-6 scroll-mt-24">
                  <h3 className="font-head font-bold text-sm mb-3 flex items-center gap-2">
                    <TierBadge tier={tier} /> <span className="text-mutext font-body text-xs">{section.titles[tier]} — {list.length} مدارس</span>
                  </h3>
                  {list.length === 0 ? (
                    <EmptyState testid={`tier-empty-${section.key}-${tier}`} text="لا توجد مدارس في هذه الشريحة بعد" />
                  ) : (
                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {list.map((s) => (
                        <SchoolCard key={s.school.id} s={s}
                          onImpersonate={impersonate} onCoordEdit={openCoordEdit} onEdit={openEdit} onRemove={remove} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      <SchoolFormModal open={!!modal} mode={modal?.mode} onClose={() => setModal(null)}
        form={form} setForm={setForm} busy={busy} onSave={save}
        coord={modal?.mode === "edit" ? modal?.school?.coordinator : null}
        coordForm={schoolCoord} setCoordForm={setSchoolCoord} sectors={sectors} />
      <CoordEditModal open={!!coordModal} mode={coordModal?.mode} schoolName={coordModal?.school?.school?.name}
        onClose={() => setCoordModal(null)} form={coordForm} setForm={setCoordForm} busy={busy} onSave={saveCoord} />
      <SchoolImportModal open={importOpen} onClose={() => setImportOpen(false)} fileRef={fileRef} busy={busy} onImport={doImport} />
      <SchoolsReportModal report={report} onClose={() => setReport(null)} />
    </div>
  );
}
